import React, { useState } from 'react';
import './HelpFAQ.css';

const HelpFAQ = () => {
  const [activeSection, setActiveSection] = useState(null);

  const toggleSection = (index) => {
    setActiveSection(activeSection === index ? null : index);
  };

  const faqData = [
    {
      category: "Getting Started",
      questions: [
        {
          question: "How do I start playing games?",
          answer: "Simply navigate to our Games section and click on any game. No registration required - just click and start playing!"
        },
        {
          question: "Do I need to create an account?",
          answer: "No account needed for most games. You'll only need to enter a username for leaderboards in games like Country Quiz and Secret Number Game."
        },
        {
          question: "Are the games really free?",
          answer: "Yes! All our games are completely free with no hidden costs or premium features."
        }
      ]
    },
    {
      category: "Game Instructions",
      questions: [
        {
          question: "How do I play the Country Quiz?",
          answer: "Enter your name, then answer questions about countries, capitals, and currencies. You have 30 seconds per question. Earn 10 points plus time bonus for correct answers. Game ends after 3 wrong answers."
        },
        {
          question: "How does the Secret Number Game work?",
          answer: "The computer selects a random number. Guess using the hints provided. 'Smaller' means your guess is too high, 'greater' means too low. Try to guess in as few attempts as possible for higher scores."
        },
        {
          question: "How do I control the Snake Game?",
          answer: "Use arrow keys to control the snake. Eat the red food while avoiding walls and your own tail. The snake grows longer as you eat more food."
        },
        {
          question: "How do I play Gem Quest?",
          answer: "Swap adjacent gems to create matches of 3 or more identical gems. Matched gems disappear and new ones fall down. Create special power-ups by matching 4 or more gems. Complete level objectives to progress through the gem-matching adventure."
        },
        {
          question: "How do image puzzles work?",
          answer: "Click on tiles next to the empty space to move them. Arrange the scrambled pieces to recreate the original tiger or lion image."
        }
      ]
    },
    {
      category: "Tools and Features",
      questions: [
        {
          question: "How do I use the Character Counter?",
          answer: "Type or paste your text into the tool, and it automatically counts characters, words, paragraphs, and lines in real-time."
        },
        {
          question: "How do I use the Greeting Cards generator?",
          answer: "Choose from various card templates, customize with your own message and design elements, then download or share your personalized greeting card. Perfect for birthdays, holidays, and special occasions."
        },
        {
          question: "What are the Spin Wheels for?",
          answer: "They're decision-making tools. Number Spinner picks random numbers, Month Spinner selects months, and Truth or Dare Spinner is perfect for party games."
        }
      ]
    },
    {
      category: "Technical Support",
      questions: [
        {
          question: "Game not loading properly?",
          answer: "Try refreshing your browser, clearing cache and cookies, or using a different browser. Ensure you have a stable internet connection."
        },
        {
          question: "Can I play on mobile?",
          answer: "Yes! All games work on smartphones and tablets. For best experience with keyboard games like Snake, use desktop."
        },
        {
          question: "Which browsers work?",
          answer: "All modern browsers: Chrome, Firefox, Safari, Edge. Use the latest version for best experience."
        }
      ]
    },
    {
      category: "Scores and Leaderboards",
      questions: [
        {
          question: "How are leaderboards calculated?",
          answer: "Players are ranked by their highest score in each game. Scores update in real-time on the results page."
        },
        {
          question: "My score didn't appear. Why?",
          answer: "Make sure you entered a username and have a stable internet connection. Scores need to be successfully submitted to appear."
        }
      ]
    },
    {
      category: "Privacy and Safety",
      questions: [
        {
          question: "What information do you collect?",
          answer: "Only usernames for leaderboards, game scores, and basic usage statistics. No personal information like emails unless you contact us."
        },
        {
          question: "Are games safe for children?",
          answer: "Yes, all games are family-friendly with no inappropriate content. We recommend parental supervision for young children."
        }
      ]
    }
  ];

  const gameGuides = [
    {
      title: "Country Quiz Strategy",
      content: "Master the geography quiz with these tips:",
      tips: [
        "Focus on major countries and their capitals first",
        "Learn currency patterns (Euro for EU countries, etc.)",
        "Use elimination - rule out obviously wrong answers",
        "Remember that time bonus adds to your score",
        "Practice regularly to improve your world knowledge"
      ]
    },
    {
      title: "Number Guessing Tips",
      content: "Become efficient at finding the secret number:",
      tips: [
        "Start with the middle number to eliminate half the range",
        "Use binary search - always pick the middle of remaining range",
        "Pay attention to hint patterns to narrow possibilities",
        "Think strategically about each guess for maximum score",
        "Higher levels give more points, focus on accuracy"
      ]
    },
    {
      title: "Snake Game Mastery",
      content: "Improve your snake game skills:",
      tips: [
        "Plan your path ahead, don't just react",
        "Use walls strategically to create movement patterns",
        "Stay near edges when snake gets long",
        "Learn to make tight spirals in limited space",
        "Practice smooth movements to avoid crashes"
      ]
    },
    {
      title: "Gem Quest Mastery",
      content: "Excel at the match-3 gem adventure:",
      tips: [
        "Look for opportunities to create special gems with 4+ matches",
        "Plan moves to create chain reactions and cascading effects",
        "Focus on level objectives rather than just high scores",
        "Use special gems strategically to clear difficult areas",
        "Take time to analyze the board before making moves"
      ]
    }
  ];

  return (
    <div className="help-page">
      <div className="help-container">
        <header className="help-header">
          <h1>Help & FAQ</h1>
          <p>Everything you need to know about Timepass Game</p>
        </header>

        <div className="help-content">
          {/* Quick Start Guide */}
          <section className="quick-start">
            <h2>🚀 Quick Start Guide</h2>
            <div className="quick-steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Choose a Game</h3>
                  <p>Browse our collection and click on any game that interests you</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Enter Your Name</h3>
                  <p>For games with leaderboards, enter a username to track your score</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Start Playing</h3>
                  <p>Follow the on-screen instructions and enjoy the game!</p>
                </div>
              </div>
            </div>
          </section>

          {/* Game Strategy Guides */}
          <section className="game-guides">
            <h2>🎯 Game Strategy Guides</h2>
            <div className="guides-grid">
              {gameGuides.map((guide, index) => (
                <div key={index} className="guide-card">
                  <h3>{guide.title}</h3>
                  <p>{guide.content}</p>
                  <ul>
                    {guide.tips.map((tip, tipIndex) => (
                      <li key={tipIndex}>{tip}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="faq-section">
            <h2>❓ Frequently Asked Questions</h2>
            {faqData.map((category, categoryIndex) => (
              <div key={categoryIndex} className="faq-category">
                <h3 className="category-title">{category.category}</h3>
                {category.questions.map((faq, faqIndex) => {
                  const globalIndex = categoryIndex * 100 + faqIndex;
                  return (
                    <div key={faqIndex} className="faq-item">
                      <button
                        className={`faq-question ${activeSection === globalIndex ? 'active' : ''}`}
                        onClick={() => toggleSection(globalIndex)}
                      >
                        {faq.question}
                        <span className="faq-icon">
                          {activeSection === globalIndex ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`faq-answer ${activeSection === globalIndex ? 'open' : ''}`}>
                        <p>{faq.answer}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </section>

          {/* Contact Support */}
          <section className="support-section">
            <h2>💬 Still Need Help?</h2>
            <div className="support-content">
              <p>
                Can't find the answer you're looking for? Our team is here to help! 
                Reach out to us through our contact form, and we'll get back to you as soon as possible.
              </p>
              <div className="support-actions">
                <a href="/contact" className="support-button primary">
                  Contact Support
                </a>
                <a href="mailto:support@timepassgame.com" className="support-button secondary">
                  Email Us
                </a>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default HelpFAQ;
